import { API_BASE_URL, WEB_BASE_URL } from "@env";
import { useAppSelector } from "@src/ducks/ducksHook";
import { selectUserEmail } from "@src/ducks/slices/user.slice";
import { largeSize, mobileSize, tabletSize } from "@src/utils/screensize-helper";
import * as faceapi from "face-api.js";
import React, { useCallback, useEffect, useRef } from "react";
import { View, useWindowDimensions } from "react-native";
import FaceDetectionCamera from "../../../account/face-id-register/FaceDetectionCamera.web";
import useStyles from "./styles.css";

type Props = {
  isLandScape: boolean;
  onScanning: () => void;
  onScanFailed: () => void;
  onScanCompleted: () => void;
  onProgress?: (value : number) => void;
}

const FaceRecognition = (props: Props) => {
  const styles = useStyles();
  const isLarge = largeSize();
  const isTablet = tabletSize();
  const isMobile = mobileSize();
  const timer = useRef<any>(null);
  const videoRef = useRef<any>(null);
  const canvasRef = useRef<any>(null);
  const { width } = useWindowDimensions();
  const email = useAppSelector(selectUserEmail);
  
  const { videoHeight, videoWidth, cameraWidth, cameraHeight } = React.useMemo(() => {
    if(isMobile){
      return {
        videoWidth: width,
        videoHeight: 651,

        cameraWidth: 1024,
        cameraHeight: 576,
      };
    }

    if(isLarge){
      return {
        videoWidth: 400,
        videoHeight: 570,
        cameraWidth: 400,
        cameraHeight: 570,
      };
    }else if(isTablet){
      return {
        videoWidth: 500,
        videoHeight: 700,
        cameraWidth: 1024,
        cameraHeight: 576,
      };
    }
    return {
      videoWidth: 500,
      videoHeight: 700,
      cameraWidth: 500,
      cameraHeight: 700,
    };
  },[width, isLarge, isTablet, isMobile, props.isLandScape]);

  const objectImages : any = [];
  const getImage = () => {
    const requestOptions = {
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json"
      },
      method: "POST",
      body: JSON.stringify({email: email})
    };
    fetch(`${API_BASE_URL}/auth/face-id`, requestOptions)
      .then((response) => response.json())
      .then((data) => {
        if(data.success){
          const item = data.data;
          if(item.detections){
            const detection = JSON.parse(item.detections[0]);
            objectImages.push(detection);
          }
        }
      });

  };

  const startVideo = () => {
    navigator.mediaDevices
      .getUserMedia( { 
        audio: false,
        video:  { 
          facingMode: "user", 
          width: cameraWidth,
          height: cameraHeight,
        } 
      })
      .then(stream => {
        const video:any = videoRef.current;
        console.log("videoRef",videoRef);
        //check if video is not null
        if(video){
          video.srcObject = stream;
          video.play();
        }
      })
      .catch(err => {
        console.error("error:", err);
      });
      
  };

  const handleVideoOnPlay = useCallback(() => {
    timer.current = startTimer();
    return () => clearInterval(timer.current);
  }, []);

  const calculateEyeAspectRatio = (eye: faceapi.Point[]) => {
    const verticalDist1 = Math.sqrt(Math.pow(eye[1].x - eye[5].x, 2) + Math.pow(eye[1].y - eye[5].y, 2));
    const verticalDist2 = Math.sqrt(Math.pow(eye[2].x - eye[4].x, 2) + Math.pow(eye[2].y - eye[4].y, 2));
    const horizontalDist = Math.sqrt(Math.pow(eye[0].x - eye[3].x, 2) + Math.pow(eye[0].y - eye[3].y, 2));
    const eyeAspectRatio = (verticalDist1 + verticalDist2) / (2 * horizontalDist);
    return eyeAspectRatio;
  };

  const isLive = (detections: faceapi.FaceDetection[]) => {
    if (detections.length === 0) {
      return false; // No faces detected
    }
  
    // Assuming only one face is detected for simplicity
    const faceLandmarks = detections[0].landmarks;
  
    // Calculate distances between key points over time
    const distances = calculateDistances(faceLandmarks);
  
    // Analyze changes in distances to determine liveness
    const movementThreshold = 7; // Adjust as needed
    const isMoving = distances.some(distance => distance > movementThreshold);

    const leftEye = faceLandmarks.getLeftEye();
    const rightEye = faceLandmarks.getRightEye();

    // Calculate the aspect ratio of each eye
    const leftEyeAspectRatio = calculateEyeAspectRatio(leftEye);
    const rightEyeAspectRatio = calculateEyeAspectRatio(rightEye);

    // Threshold to determine if a blink is occurring
    const blinkThreshold = 0.27; // Adjust as needed
    // Check if the aspect ratio of both eyes is below the threshold
    const isBlink = leftEyeAspectRatio < blinkThreshold && rightEyeAspectRatio < blinkThreshold;
    return  isBlink;//|| isMoving;
  };
  
  const calculateDistances = (landmarks: faceapi.FaceLandmarks68) => {
    const keyPoints = landmarks.positions;
    const distances = [];
  
    for (let i = 0; i < keyPoints.length - 1; i++) {
      for (let j = i + 1; j < keyPoints.length; j++) {
        const dx = keyPoints[i].x - keyPoints[j].x;
        const dy = keyPoints[i].y - keyPoints[j].y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        distances.push(distance);
      }
    }
  
    return distances;
  };

  const startTimer = () => {
    let matches = 0;
    let distanceCameraType = 0;
    const eyeBlinking = false;
    let notDetectedCtr = 0;
    
    return setInterval(async () => {
      // ==============================================================
      const video:any = videoRef.current;
      if (faceapi.nets.ssdMobilenetv1.isLoaded &&
              faceapi.nets.faceLandmark68Net.isLoaded &&
                faceapi.nets.faceRecognitionNet.isLoaded) {
        const labeledFaceDescriptors = await Promise.all(objectImages[0].map(async(data : any) => {
          const faceDescriptors = new Float32Array(Object.values(data.descriptor));
          const faces = [faceDescriptors];
          return new faceapi.LabeledFaceDescriptors("",faces) ;
        })
        );
        // Create a face detection options object with a minimum confidence threshold
        const detectionOptions = new faceapi.SsdMobilenetv1Options({ minConfidence: 0.85 });
        // console.log("labeledFaceDescriptors --", labeledFaceDescriptors);
        const detections = await faceapi.detectAllFaces(video, detectionOptions).withFaceLandmarks().withFaceDescriptors();
        if (detections.length > 0 ) {
          const maxDescriptorDistance = 0.4;
          if(isLive(detections) || eyeBlinking){
            if(labeledFaceDescriptors.length > 0){

              const faceMatcher = new faceapi.FaceMatcher(labeledFaceDescriptors, maxDescriptorDistance);
              const results = detections.map(fd => faceMatcher.findBestMatch(fd.descriptor));
              results.forEach((bestMatch, i) => {
                const box = detections[i].detection.box;
                const text = bestMatch.label;
                      
                if ((!text.includes("unknown") && detections[i].alignedRect.score > 0.90 &&
                     (box.area / 1000) > 40  && (box.area / 1000) < 95)) {
                  
                  if ((box.area / 1000) > 40  && (box.area / 1000) < 50) {
                    if (distanceCameraType != 1) matches = 0;
                    matches += 1;
                    distanceCameraType = 1;
                  }
                  else if ((box.area / 1000) > 50  && (box.area / 1000) < 60) {
                    if (distanceCameraType != 2) matches = 0;
                    matches += 1;
                    distanceCameraType = 2;
                  }
                  else if ((box.area / 1000) > 60  && (box.area / 1000) < 70) {
                    if (distanceCameraType != 3) matches = 0;
                    matches += 1;
                    distanceCameraType = 3;
                  }
                  else if ((box.area / 1000) > 70  && (box.area / 1000) < 80) {   
                    if (distanceCameraType != 4) matches = 0;
                    matches += 1;
                    distanceCameraType = 4;
                  }
                  else if ((box.area / 1000) > 80  && (box.area / 1000) < 95) {  
                    if (distanceCameraType != 5) matches = 0;
                    matches += 1;
                    distanceCameraType = 5;
                  }
                  else {
                    matches = 0;
                    distanceCameraType = 0;
                  }
                  if (1 >= matches) {
                    stopVideo();
                    props.onScanCompleted();
                  }
                }
                else {
                  matches = 0;
                  distanceCameraType = 0;
                }
              });
            }
          }
        } else {
          if(videoRef.current){
            notDetectedCtr += 1;
          }
          // if(notDetectedCtr >= 3){
          //   console.log("onScanFailed");
          //   stopVideo(props.onScanFailed());
          //   notDetectedCtr = 0;
          // }
        }
      }
    }, 100);
  };

  const stopVideo = (arg1: any = null) => {
    timer.current && clearInterval(timer.current);
    if(videoRef.current){
      videoRef.current.pause();
      typeof arg1 === "function" && arg1();
      if (videoRef.current.srcObject) {
        videoRef.current.srcObject.getTracks().forEach((track :any) => track.stop());
      }
    }
  };

  const loadModels = async () => {
    const MODEL_URL = `${WEB_BASE_URL}/models/`;
    Promise.all([
      faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL),
      faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
      faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      faceapi.nets.faceExpressionNet.loadFromUri(MODEL_URL),
    ]).then(() => {
      console.log("Successfully loaded..");
      props.onScanning();
    })
      .catch(error => {
        console.log("Still error and no luck: ", JSON.stringify(error));
      });
  };

  useEffect(() => {
    loadModels();
    getImage();
    startVideo();
    return () => stopVideo();
  }, []);

  return (
    <View style={[styles.v_camera_view, { width: videoWidth, height: videoHeight}]}>
      <FaceDetectionCamera 
        canvasRef={canvasRef} 
        cameraRef={videoRef}
        height={videoHeight}
        width={videoWidth}
        cameraOnPlay={handleVideoOnPlay} />
    </View>
  );
};

export default FaceRecognition;
